import moment from "moment"
import { Button, Col, Dropdown, Form, Row } from "react-bootstrap"
import { ChevronDown, ChevronUp } from "react-bootstrap-icons"
import { ColumnDescription } from "react-bootstrap-table-next"
import ToolkitProvider, { CSVExport } from "react-bootstrap-table2-toolkit"
import { useTranslation } from "react-i18next"
import Select from "react-select"
import { ValueType } from "react-select/lib/types"
import CustomTotal from "../../components/Tables/CustomTotal"
import pageListRenderer from "../../components/Tables/PageListRenderer"
import RemotePagination from "../../components/Tables/RemotePagination"
import sizePerPageOptionRenderer from "../../components/Tables/SizePerPageOptionRenderer"
import { GLOBAL_CONSTANTS, PARCEL_CONSTANTS, PAYMENT_PENDING } from "../../constants/constants"
import ImagesViewer from "./ImagesViewer"

const ParcelsTable = ({
    parcels,
    tableDataChange,
    parcelSearch,
    setParcelSearch,
    onSearchClick,
    handleShow,
    parcelDestinations,
    parcelStatus,
    onDeleteParcelClick
}: {
    parcels: Record<string, any>
    tableDataChange: Function
    parcelSearch: any
    setParcelSearch: Function
    onSearchClick: Function
    handleShow: Function
    parcelDestinations: Record<string, any>
    parcelStatus: any
    onDeleteParcelClick: Function
}) => {
    const { t } = useTranslation()

    function parcelCreatedDate(cell: string, showTime = false) {
        if (cell) return moment(cell).format(showTime ? "DD MMM YYYY, h:mm a" : "DD MMM YYYY")
    }

    const onExternalClickLink = (id: string, type: string) => {
        let url =
            type === "barcode"
                ? GLOBAL_CONSTANTS.BARCODE_DOWNLOAD_URL + id
                : type === "invoice"
                  ? GLOBAL_CONSTANTS.INVOICE_DOWNLOAD_URL + id
                  : ""
        window.open(url, "_blank")
    }

    const onDeleteParcel = (parcelId: number) => {
        onDeleteParcelClick(parcelId)
    }

    const handleChange = (option: ValueType<any>) => {
        if (!["split", "notsplit", "deleted", "notdeleted"].includes(option.value)) {
            setParcelSearch({
                ...parcelSearch,
                status: option,
                split: "",
                deleted: ""
            })
        } else {
            switch (option.value) {
                case "split":
                    setParcelSearch({ ...parcelSearch, split: "true" })
                    break
                case "notsplit":
                    setParcelSearch({ ...parcelSearch, split: "false" })
                    break
                case "deleted":
                    setParcelSearch({ ...parcelSearch, deleted: "1" })
                    break
                case "notdeleted":
                    setParcelSearch({ ...parcelSearch, deleted: "0" })
                    break
                default:
                    break
            }
        }
    }

    const parcelsColumns = [
        {
            dataField: "id",
            text: t("#")
        },
        {
            dataField: "uuid",
            text: t("Consol ID"),
            classes: "highlighted-text",
            formatter: (cell: string, row: Record<string, any>) => {
                return (
                    <span>
                        <p>{cell}</p>
                        <small className="d-block text-muted mx-w-10">{row?.awb}</small>
                    </span>
                )
            }
        },
        {
            dataField: "Parent",
            text: t("Parent ID"),
            classes: "highlighted-text",
            formatter: (cell: Record<string, any>, row: Record<string, any>) => {
                return cell ? <span>{cell?.uuid}</span> : "-"
            }
        },
        {
            text: t("Items"),
            dataField: "ParcelItems",
            formatter: (cell: Record<string, any>[], row: Record<string, any>) => {
                return cell?.length
            },
            csvFormatter: (cell: Record<string, any>[]) => {
                return cell?.length
            }
        },
        {
            dataField: "ParcelAddress.code",
            text: t("Destination"),
            formatter: (cell: string) => {
                // return cell && cell !== "" ? (
                //     <OverlayTrigger
                //         placement="bottom"
                //         delay={{ show: 250, hide: 400 }}
                //         overlay={
                //             <Tooltip id={`${cell}-tooltip`}>
                //                 {countryNamesInEnglish.of(cell)}
                //             </Tooltip>
                //         }
                //     >
                //         <span className="tr-pointer">{cell}&nbsp;</span>
                //     </OverlayTrigger>
                // ) : (
                //     "-"
                // )
                return cell && cell !== "" ? cell : " - "
            },
            csvFormatter: (cell: string | number, row: Record<string, any>) => {
                return cell ? cell : " - "
            }
        },
        {
            text: t("Customer"),
            dataField: "user.name",
            formatter: (cell: string | number, row: Record<string, any>) => {
                return cell ? (
                    <div>
                        <span className="d-flex">{row?.user?.user_pin}</span>
                        <span>{cell}</span>
                    </div>
                ) : (
                    ""
                )
            },
            csvFormatter: (cell: string | number, row: Record<string, any>) => {
                return cell ? row?.user?.user_pin + " ," + cell : ""
            }
        },
        {
            text: t("Suite"),
            dataField: "ParcelSuite.suite.suite",
            formatter: (cell: string | number) => {
                return cell ? cell : "-"
            },
            csvFormatter: (cell: string | number, row: Record<string, any>) => {
                return cell ? cell : "-"
            }
        },
        {
            dataField: "amount",
            text: t("Value"),
            sort: true,
            formatter: (cell: string | number, row: Record<string, any>) => {
                return cell && ![2, 3].includes(Number(row?.parcel_type_id)) ? (
                    <div>
                        <strong>{cell}</strong> {row?.ParcelPayment?.currency}
                    </div>
                ) : row?.ParcelItems?.length > 0 ? (
                    <div>
                        <strong>
                            {row?.ParcelItems?.reduce(
                                (a: Record<string, any>, b: Record<string, any>) =>
                                    (parseFloat(a.total_amount) || 0) + (parseFloat(b.total_amount) || 0),
                                0
                            )}
                        </strong>{" "}
                        {row?.ParcelPayment?.currency}
                    </div>
                ) : (
                    ""
                )
            },
            csvFormatter: (cell: string | number, row: Record<string, any>) => {
                return cell ? cell + " " + row?.ParcelPayment?.currency : ""
            }
        },
        {
            text: t("Status"),
            sort: true,
            dataField: "ParcelHasStatuses",
            formatter: (cell: any, row: Record<string, any>) => {
                const status = cell.filter((data: Record<string, any>) => data.active === 1)
                return status && status.length > 0 ? (
                    <div
                        className={
                            row?.deleted
                                ? "deleted"
                                : status[0]?.ParcelStatus?.name?.toLowerCase()?.replaceAll(/[^a-zA-Z]/g, "-")
                        }>
                        {row?.deleted ? "Deleted" : status[0]?.ParcelStatus?.name}
                    </div>
                ) : (
                    ""
                )
            },
            csvFormatter: (cell: any, row: Record<string, any>) => {
                const status = cell.filter((data: Record<string, any>) => data.active === 1)
                return status && status.length > 0 ? status[0]?.ParcelStatus?.name : ""
            }
        },
        {
            text: t("Split"),
            dataField: "",
            formatter: (cell: any, row: Record<string, any>) => {
                return Number(row?.split_from_id) > 0 || row?.SplitParcels?.length > 0 ? (
                    <div className="status">{t("yes")}</div>
                ) : (
                    <div className="expired">{t("no")}</div>
                )
            }
        },
        {
            dataField: "created_at",
            text: t("DATE"),
            sort: true,
            formatter: (cell: string, row: Record<string, any>) => {
                const dateAndTime = parcelCreatedDate(cell, true)?.split(",") || []
                const date = dateAndTime[0].trim()
                const time = dateAndTime[1].trim()
                return (
                    <>
                        <span className="d-block">{date}</span>
                        <small className="text-muted">{time}</small>
                    </>
                )
            },
            csvFormatter: (cell: string, row: Record<string, any>) => {
                return parcelCreatedDate(cell, true)
            }
        },
        {
            dataField: "",
            text: t("Actions"),
            csvExport: false,
            formatter: (cell: number, row: Record<string, any>) => {
                const status = row.ParcelHasStatuses?.find((data: Record<string, any>) => data.active === 1)
                return (
                    <Dropdown className="actions-menu">
                        <Dropdown.Toggle variant="primary" id="dropdown-basic" size={"sm"} className="py-0">
                            {t("Actions")}
                        </Dropdown.Toggle>

                        <Dropdown.Menu>
                            {[
                                "editParcel",
                                row?.ParcelItems?.length === 0 ||
                                !row?.ParcelItems?.find((pi: Record<string, any>) => pi?.quantity > 1)?.id ||
                                row?.parcel_type_id > 1 ||
                                row?.parcel_status_id !== PAYMENT_PENDING
                                    ? null
                                    : "splitParcel",
                                "changeAddress",
                                "printBarcode",
                                row?.parcel_status_id >= 3 && row?.parcel_status_id < 31
                                    ? "downloadInvoice"
                                    : null,
                                "markException"
                            ]
                                .filter((p) => p)
                                .map((action) => (
                                    <Dropdown.Item className="py-0">
                                        {action === "printBarcode" ? (
                                            <div onClick={() => onExternalClickLink(row.uuid, "barcode")}>
                                                {t(action)}
                                            </div>
                                        ) : action === "downloadInvoice" ? (
                                            <div onClick={() => onExternalClickLink(row?.uuid, "invoice")}>
                                                {t("Download Invoice")}
                                            </div>
                                        ) : (
                                            <div onClick={() => handleShow(action, row)}>{t(`${action}`)}</div>
                                        )}
                                    </Dropdown.Item>
                                ))}
                            {[PARCEL_CONSTANTS.PENDING_PAYMENT, PARCEL_CONSTANTS.RECEIVED].includes(
                                status?.parcel_status_id
                            ) && (
                                <Dropdown.Item className="py-0">
                                    <div onClick={() => onDeleteParcel(row?.id)}>{t("Remove Parcel")}</div>
                                </Dropdown.Item>
                            )}
                        </Dropdown.Menu>
                    </Dropdown>
                )
            }
        },
        // export only columns
        {
            dataField: "awb",
            text: t("AWB"),
            hidden: true
        },
        {
            dataField: "ParcelShipmentTracking.tracking_number",
            text: t("Tracking"),
            hidden: true,
            csvFormatter: (cell: string | number, row: Record<string, any>) => {
                return cell ? cell : "-"
            }
        },
        {
            dataField: "Website.name",
            text: t("Supplier"),
            hidden: true,
            csvFormatter: (cell: string | number, row: Record<string, any>) => {
                return cell ? cell : "-"
            }
        },

        {
            dataField: "dimension_length",
            text: t("Dimensions"),
            hidden: true,
            csvFormatter: (cell: string | number, row: Record<string, any>) => {
                const w = row.dimension_width
                const h = row.dimension_height
                return cell ? cell + " x " + w + " x " + h + "  " + row.dimensions_unit : "-"
            }
        },

        {
            dataField: "weight_value",
            text: t("Actual_Weight"),
            hidden: true,
            csvFormatter: (cell: string | number, row: Record<string, any>) => {
                return cell ? cell + "  " + row.weight_unit : "-"
            }
        }
    ]

    const expandRow = {
        renderer: (row: Record<string, any>): JSX.Element => {
            const actualWeight = row.weight_value
            const l = row.dimension_length
            const w = row.dimension_width
            const h = row.dimension_height

            // let volumeWeight = GetVolumeWeight(row)
            return (
                <Row className="expanding-row px-4 py-4">
                    <Col xs={12} sm={12} md={12} lg={5}>
                        <Row className="expand-data">
                            <Col xs={12} sm={12} md={12} lg={5} className="label">
                                {t("AWB")}
                            </Col>
                            <Col xs={12} sm={12} md={12} lg={7} className="value">
                                {row.awb}
                            </Col>
                        </Row>
                        <Row className="expand-data">
                            <Col xs={12} sm={12} md={12} lg={5} className="label">
                                {t("Tracking")}
                            </Col>
                            <Col xs={12} sm={12} md={12} lg={7} className="value">
                                {row.ParcelShipmentTracking?.tracking_number
                                    ? row.ParcelShipmentTracking.tracking_number
                                    : "-"}
                            </Col>
                        </Row>
                        <Row className="expand-data">
                            <Col xs={12} sm={12} md={12} lg={5} className="label">
                                {t("Supplier")}
                            </Col>
                            <Col xs={12} sm={12} md={12} lg={7} className="value">
                                {row?.Website?.name ? row.Website.name : "-"}
                            </Col>
                        </Row>
                        <Row className="expand-data">
                            <Col xs={12} sm={12} md={12} lg={5} className="label">
                                {t("Created Day")}
                            </Col>
                            <Col xs={12} sm={12} md={12} lg={7} className="value">
                                {moment(row.created_at).format("DD MMM yyyy hh:mm A")}
                            </Col>
                        </Row>
                    </Col>
                    <Col xs={12} sm={12} md={12} lg={5}>
                        <Row className="expand-data">
                            <Col xs={12} sm={12} md={12} lg={5} className="label">
                                {t("Actual_Weight")}
                            </Col>
                            <Col xs={12} sm={12} md={12} lg={7} className="value">
                                <strong>{actualWeight ? actualWeight : ""} </strong>
                                {row.weight_unit ? row.weight_unit : "-"}
                            </Col>
                        </Row>
                        <Row className="expand-data">
                            <Col xs={12} sm={12} md={12} lg={5} className="label">
                                {t("Dimensions")}
                            </Col>
                            <Col xs={12} sm={12} md={12} lg={7} className="value">
                                {l} {l && <i className="bx bx-x"></i>}
                                {w} {w && <i className="bx bx-x"></i>}
                                {h} {l && w && h ? row.dimensions_unit : "-"}
                            </Col>
                        </Row>
                        <Row className="expand-data">
                            <Col xs={12} sm={12} md={12} lg={5} className="label">
                                {t("Chargeable_Weight")}
                            </Col>
                            <Col xs={12} sm={12} md={12} lg={7} className="value">
                                <strong>{row?.chargeable_weight?.toFixed(2)} </strong>
                                {row.weight_unit ? row.weight_unit : ""}
                            </Col>
                        </Row>
                        {row?.ParcelFiles?.length > 0 && (
                            <Row className="expand-data">
                                <Col xs={12} sm={12} md={12} lg={5} className="label">
                                    {t("Parcel Files")}
                                </Col>
                                <Col xs={12} sm={12} md={12} lg={7}>
                                    <ImagesViewer parcel={row} />
                                </Col>
                            </Row>
                        )}
                        {/* <Row className="expand-data">
							<Col xs={12} sm={12} md={12} lg={5} className="label">
								{t("Last Update")}
							</Col>
							<Col xs={12} sm={12} md={12} lg={7} className="value">
								{moment(row.updated_at).format("DD MMM yyyy hh:mm A")}
							</Col>
						</Row> */}
                    </Col>
                </Row>
            )
        },
        showExpandColumn: true,
        expandByColumnOnly: true,
        expandHeaderColumnRenderer: ({ isAnyExpands }: { isAnyExpands: boolean }) => {
            return <></>
        },
        expandColumnRenderer: ({ expanded }: { expanded: boolean }) => {
            if (expanded) {
                return <ChevronUp color="#2D3748" size={10} className="mx-2 my-3 arrow-icon" />
            }
            return <ChevronDown color="#2D3748" size={10} className="mx-2 my-3 arrow-icon" />
        }
    }

    const options = {
        sizePerPage: 50,
        hideSizePerPage: false,
        hidePageListOnlyOnePage: true,
        sizePerPageOptionRenderer,
        showTotal: true,
        pageListRenderer,
        paginationTotalRenderer: CustomTotal
    }
    const handleTableChange = (type: any, { page, sizePerPage }: { page: number; sizePerPage: number }) => {
        tableDataChange(page, sizePerPage)
    }

    const { ExportCSVButton } = CSVExport

    return (
        <div className="table-responsive px-4">
            {parcels?.parcels?.length > 0 && parcelsColumns && (
                <ToolkitProvider
                    keyField="uuid"
                    data={parcels?.parcels}
                    columns={parcelsColumns as ColumnDescription<any, any>[]}
                    search={{ searchFormatted: true }}
                    exportCSV={{
                        fileName: "ParcelTable.csv",
                        noAutoBOM: false,
                        exportAll: false,
                        onlyExportFiltered: true
                    }}>
                    {(props) => {
                        return (
                            <div className="table-container my-4">
                                <Row className="search dispatch-search">
                                    <Col xs={12} sm={12} md={12} lg={12}>
                                        <Row>
                                            <Col xs={12} sm={6} md={6} lg={2} className="px-3">
                                                <input
                                                    className="form-control"
                                                    type="text"
                                                    placeholder={"Search By ID/AWB"}
                                                    name="uuid"
                                                    value={parcelSearch?.uuid}
                                                    onChange={(e) =>
                                                        setParcelSearch({
                                                            ...parcelSearch,
                                                            [e.target.name]: e.target.value
                                                        })
                                                    }
                                                />
                                            </Col>
                                            <Col xs={12} sm={6} md={6} lg={2}>
                                                <Form.Select
                                                    name="country"
                                                    onChange={(e: any) =>
                                                        setParcelSearch({
                                                            ...parcelSearch,
                                                            [e.target.name]: e.target.value
                                                        })
                                                    }>
                                                    <option value="">{t("Search By Destination")}</option>
                                                    {parcelDestinations?.map((destinations: any) => (
                                                        <option
                                                            key={destinations?.iso2_code}
                                                            selected={
                                                                destinations?.iso2_code ===
                                                                parcelSearch?.country
                                                            }
                                                            value={destinations?.iso2_code}>
                                                            {destinations?.name}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </Col>
                                            <Col xs={12} sm={6} md={6} lg={2}>
                                                <input
                                                    className="form-control"
                                                    type="text"
                                                    placeholder={"Search By customer"}
                                                    name="customer"
                                                    value={parcelSearch?.customer}
                                                    onChange={(e) =>
                                                        setParcelSearch({
                                                            ...parcelSearch,
                                                            [e.target.name]: e.target.value
                                                        })
                                                    }
                                                />
                                            </Col>
                                            <Col xs={12} sm={6} md={6} lg={2}>
                                                <input
                                                    className="form-control"
                                                    type="text"
                                                    placeholder={"Search By Suite Id"}
                                                    name="suite"
                                                    value={parcelSearch?.suite}
                                                    onChange={(e) =>
                                                        setParcelSearch({
                                                            ...parcelSearch,
                                                            [e.target.name]: e.target.value
                                                        })
                                                    }
                                                />
                                            </Col>
                                            <Col xs={12} sm={6} md={6} lg={2}>
                                                <Select
                                                    options={[...parcelStatus]}
                                                    name="status"
                                                    placeholder={t("Search By Status")}
                                                    onChange={handleChange}
                                                    defaultValue={
                                                        parcelSearch?.status?.value
                                                            ? parcelSearch?.status
                                                            : parcelSearch?.split && parcelSearch?.split !== ""
                                                              ? parcelStatus?.find((s: Record<string, any>) => {
                                                                    if (parcelSearch?.split === "true") {
                                                                        return s.value === "split"
                                                                    } else {
                                                                        return s.value === "notsplit"
                                                                    }
                                                                })
                                                              : parcelSearch?.deleted &&
                                                                  parcelSearch?.deleted !== ""
                                                                ? parcelStatus?.find(
                                                                      (s: Record<string, any>) => {
                                                                          if (parcelSearch?.deleted === "1") {
                                                                              return s.value === "deleted"
                                                                          } else {
                                                                              return s.value === "notdeleted"
                                                                          }
                                                                      }
                                                                  )
                                                                : ""
                                                    }
                                                    value={
                                                        parcelSearch?.status?.value
                                                            ? parcelSearch?.status
                                                            : parcelSearch?.split && parcelSearch?.split !== ""
                                                              ? parcelStatus?.find((s: Record<string, any>) => {
                                                                    if (parcelSearch?.split === "true") {
                                                                        return s.value === "split"
                                                                    } else {
                                                                        return s.value === "notsplit"
                                                                    }
                                                                })
                                                              : parcelSearch?.deleted &&
                                                                  parcelSearch?.deleted !== ""
                                                                ? parcelStatus?.find(
                                                                      (s: Record<string, any>) => {
                                                                          if (parcelSearch?.deleted === "1") {
                                                                              return s.value === "deleted"
                                                                          } else {
                                                                              return s.value === "notdeleted"
                                                                          }
                                                                      }
                                                                  )
                                                                : ""
                                                    }
                                                />
                                            </Col>
                                            <Col xs={12} sm={6} md={6} lg={2}>
                                                <Button
                                                    id="btn-primary"
                                                    className="btn btn-primary w-100"
                                                    type="submit"
                                                    onClick={(e: any) => onSearchClick()}>
                                                    {t("Search")}
                                                </Button>
                                            </Col>
                                        </Row>
                                    </Col>
                                    <Col className="d-none">
                                        <ExportCSVButton
                                            {...props.csvProps}
                                            className=" parcelExportButton d-none">
                                            Export
                                        </ExportCSVButton>
                                    </Col>
                                </Row>
                                <Row className="table-data my-3">
                                    <Col>
                                        {parcels?.parcels?.length > 0 && (
                                            <RemotePagination
                                                data={parcels?.parcels}
                                                page={parcels?.page}
                                                sizePerPage={parcels?.page_size}
                                                totalSize={parcels?.total}
                                                onTableChange={handleTableChange}
                                                columns={parcelsColumns}
                                                keyField="uuid"
                                                expandRow={expandRow}
                                                props={props}
                                            />
                                        )}
                                    </Col>
                                </Row>
                            </div>
                        )
                    }}
                </ToolkitProvider>
            )}
        </div>
    )
}

export default ParcelsTable
